import { BrowserRouter, Navigate, Route, Routes } from "react-router"

import { AuthProvider } from "@/auth/AuthProvider"
import { ProtectedRoute } from "@/auth/ProtectedRoute"
import { AppShell } from "@/layouts/AppShell"
import LoginPage from "@/pages/login/LoginPage"
import { RegisterPage } from "@/pages/register/RegisterPage"
import AccountsPage from "@/pages/accounts/AccountsPage"
import AdminPanelPage from "@/pages/admin/AdminPanelPage"
import AdminLogsTab from "@/pages/admin/logs/AdminLogsTab"
import SelectTikTokAccountsPage from "@/pages/admin/SelectTikTokAccountsPage"
import UsersTab from "@/pages/admin/users/UsersTab"
import AccountsTab from "@/pages/admin/accounts/AccountsTab"
import DomainsTab from "@/pages/admin/domains/DomainsTab"
import PixelsTab from "@/pages/admin/pixels/PixelsTab"
import AdNetworksTab from "@/pages/admin/networks/AdNetworksTab"
import ActivityPage from "@/pages/activity/ActivityPage"
import PostbackTab from "@/pages/activity/postback/PostbackTab"
import CampaignJobsTab from "@/pages/activity/campaignjobs/CampaignJobsTab"
import AdsManagerPage from "@/pages/adsmanager/AdsManagerPage"
import DashboardPage from "@/pages/dashboard/DashboardPage"
import FormPage from "@/pages/form/FormPage"
import OffersPage from "@/pages/offers/OffersPage"
import ProfilePage from "@/pages/profile/ProfilePage"
import ToolsPage, { ToolsIndexRedirect } from "@/pages/tools/ToolsPage"
import ArticleGeneratorTab from "@/pages/tools/article-generator/ArticleGeneratorTab"
import ArticleArchiveTab from "@/pages/tools/article-generator/ArticleArchiveTab"
import ArticleArchiveDetailTab from "@/pages/tools/article-generator/ArticleArchiveDetailTab"
import UrlEncoderTab from "@/pages/tools/url-encoder/UrlEncoderTab"
import CreosListPage from "@/pages/creos/CreosListPage"
import CreosNewPage from "@/pages/creos/CreosNewPage"
import CreosDetailPage from "@/pages/creos/CreosDetailPage"
import CreosLayout, { CreosAdminOnly } from "@/pages/creos/CreosLayout"
import CreosUniquifyPage from "@/pages/creos/CreosUniquifyPage"
import CreosUniquifyBatchDetailPage from "@/pages/creos/CreosUniquifyBatchDetailPage"
import CreosUniquifyDetailPage from "@/pages/creos/CreosUniquifyDetailPage"
import RequestsPage from "@/pages/requests/RequestsPage"
import TopUpRequestsTab from "@/pages/requests/TopUpRequestsTab"
import AccountRequestsTab from "@/pages/requests/AccountRequestsTab"

/**
 * Top-level route tree. Public routes sit outside `<AppShell/>`;
 * everything else is wrapped in `<ProtectedRoute/>` so unauth'd
 * users bounce to `/login` before the shell ever mounts.
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/*
           * Select TikTok Accounts — lives OUTSIDE the protected
           * subtree because the OAuth callback hops here with only a
           * `?session=<token>` query param (no JWT cookie guaranteed).
           * Skips `<AppShell/>` and renders its own layout.
           */}
          <Route
            path="/admin/tiktok/select-accounts"
            element={<SelectTikTokAccountsPage />}
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />

            <Route path="accounts" element={<AccountsPage />} />
            <Route path="adsmanager" element={<AdsManagerPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="form" element={<FormPage />} />
            <Route path="offers" element={<OffersPage />} />
            <Route path="tools" element={<ToolsPage />}>
              <Route index element={<ToolsIndexRedirect />} />
              <Route path="url-encoder" element={<UrlEncoderTab />} />
              <Route path="article-generator" element={<ArticleGeneratorTab />} />
              <Route path="article-generator/archive" element={<ArticleArchiveTab />} />
              <Route
                path="article-generator/archive/:id"
                element={<ArticleArchiveDetailTab />}
              />
            </Route>
            <Route path="profile" element={<ProfilePage />} />

            <Route
              path="requests"
              element={
                <ProtectedRoute requiredRole="admin">
                  <RequestsPage />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="top-up" replace />} />
              <Route path="top-up" element={<TopUpRequestsTab />} />
              <Route path="accounts" element={<AccountRequestsTab />} />
            </Route>

            <Route path="creos" element={<CreosLayout />}>
              <Route
                index
                element={
                  <CreosAdminOnly>
                    <CreosListPage />
                  </CreosAdminOnly>
                }
              />
              <Route
                path="new"
                element={
                  <CreosAdminOnly>
                    <CreosNewPage />
                  </CreosAdminOnly>
                }
              />
              <Route path="uniquify" element={<CreosUniquifyPage />} />
              <Route
                path="uniquify/batch/:batchId"
                element={<CreosUniquifyBatchDetailPage />}
              />
              <Route path="uniquify/:id" element={<CreosUniquifyDetailPage />} />
              <Route
                path=":id"
                element={
                  <CreosAdminOnly>
                    <CreosDetailPage />
                  </CreosAdminOnly>
                }
              />
            </Route>

            <Route
              path="activity"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ActivityPage />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="logs" replace />} />
              <Route path="sync" element={<Navigate to="../logs" replace />} />
              <Route path="postback" element={<PostbackTab />} />
              <Route path="logs" element={<AdminLogsTab />} />
              <Route path="campaign-jobs" element={<CampaignJobsTab />} />
            </Route>

            <Route
              path="admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPanelPage />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<UsersTab />} />
              <Route path="accounts" element={<AccountsTab />} />
              <Route path="domains" element={<DomainsTab />} />
              <Route path="pixels" element={<PixelsTab />} />
              <Route path="ad-networks" element={<AdNetworksTab />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
